import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Package, AlertTriangle, CheckCircle, Box, PackageOpen } from "lucide-react";
import { useCartStore } from "@/stores/cart-store";
import { useTruckStore } from "@/stores/truck-store";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { TruckLoadingVisualization } from "./truck-loading-visualization";

interface CartonizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PalletSummaryRow {
  modelId: string;
  brand: string;
  modelName: string;
  quantity: number;
  unitsPerLayer: number;
  palletsRequired: number;
  palletLayerWidthIn?: number | null;
  palletLayerDepthIn?: number | null;
}

export function CartonizationDialog({ open, onOpenChange }: CartonizationDialogProps) {
  const { items } = useCartStore();
  const { selectedTruckSize, calculatorMode, getTruckDimensions } = useTruckStore();
  
  const cartonizationMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/cartonization/calculate', {
        method: 'POST',
        body: JSON.stringify({
          cartItems: items.map(item => ({
            id: item.id,
            modelId: item.modelId,
            inventoryId: item.inventoryId,
            quantity: item.quantity,
            dimensions: item.dimensions,
            brand: item.brand,
            modelName: item.modelName,
            grade: item.grade
          })),
          truckSize: selectedTruckSize,
          mode: calculatorMode
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to calculate cartonization');
      }
      
      return response.json();
    }
  });

  const handleCalculate = () => {
    cartonizationMutation.mutate();
  };

  const truckDimensions = getTruckDimensions();
  const result = cartonizationMutation.data;
  const isLoading = cartonizationMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cartonization-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {calculatorMode === '2d' ? (
              <Box className="h-5 w-5" />
            ) : (
              <PackageOpen className="h-5 w-5" />
            )}
            Truck Loading Analysis
          </DialogTitle>
          <DialogDescription>
            {calculatorMode === '2d' 
              ? 'Simple 2D floor planning calculation' 
              : 'Advanced 3D optimization with Paxter AI'}
          </DialogDescription>
        </DialogHeader>

        {/* Current Settings */}
        <div className="bg-secondary/30 p-4 rounded-lg space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Truck Size:</span>
            <span className="font-medium">{truckDimensions?.name || 'Unknown'}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Calculator Mode:</span>
            <Badge variant={calculatorMode === '3d' ? 'default' : 'secondary'}>
              {calculatorMode === '2d' ? '2D Simple' : '3D Advanced'}
            </Badge>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Items in Cart:</span>
            <span className="font-medium">{items.length}</span>
          </div>
        </div>

        {/* Calculate Button */}
        {!result && (
          <div className="flex justify-center py-4">
            <Button 
              onClick={handleCalculate} 
              disabled={isLoading || items.length === 0}
              size="lg"
              data-testid="button-calculate-cartonization"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Calculate Truck Loading
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-8 space-y-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {calculatorMode === '3d' 
                ? 'Running Paxter 3D optimization...' 
                : 'Calculating 2D floor plan...'}
            </p>
          </div>
        )}

        {/* Results */}
        {result && !isLoading && (
          <div className="space-y-4">
            {/* Fallback Warning */}
            {result.fallbackUsed && (
              <Alert variant="destructive" data-testid="alert-fallback">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  3D optimization unavailable ({result.fallbackReason}). Showing 2D results instead.
                </AlertDescription>
              </Alert>
            )}

            {/* 2D Results */}
            {(result.mode === '2d' || result.fallbackUsed) && result.result && (
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  2D Floor Plan Results
                </h3>
                
                <div className="bg-secondary/30 p-4 rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Capacity Used:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-lg">
                        {Math.round(result.result.capacityUsedPercentage)}%
                      </span>
                      {result.result.canFit ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                      )}
                    </div>
                  </div>

                  <div className="w-full bg-secondary rounded-full h-4 overflow-hidden">
                    <div 
                      className={`h-full transition-all ${
                        result.result.warningLevel === 'high' 
                          ? 'bg-destructive' 
                          : result.result.warningLevel === 'medium' 
                          ? 'bg-yellow-500' 
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(result.result.capacityUsedPercentage, 100)}%` }}
                    />
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-muted-foreground mb-1">Area Used</div>
                      <div className="font-semibold">
                        {Math.round(result.result.totalAreaUsed).toLocaleString()} sq in
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground mb-1">Remaining</div>
                      <div className="font-semibold">
                        {result.result.remainingArea === Infinity 
                          ? 'Unlimited' 
                          : `${Math.round(result.result.remainingArea).toLocaleString()} sq in`}
                      </div>
                    </div>
                  </div>

                  {!result.result.canFit && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Items exceed truck capacity. Consider selecting a larger truck or removing items.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                {/* Visual Truck Loading Diagram */}
                {result.result.placedItems && result.result.truckDimensions && (
                  <>
                    <TruckLoadingVisualization 
                      placedItems={result.result.placedItems}
                      truckDimensions={result.result.truckDimensions}
                    />
                    
                    {/* Show warning for unplaced items */}
                    {result.result.unplacedCount && result.result.unplacedCount > 0 && (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          {result.result.unplacedCount} item{result.result.unplacedCount > 1 ? 's' : ''} could not be placed in the truck. They are too large or there is insufficient remaining space.
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}

                {/* Pallet summary */}
                {result.result.palletSummary && result.result.palletSummary.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <Package className="h-4 w-4" /> Pallet Requirements
                    </h4>
                    <div className="rounded-md border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-secondary/50">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-xs">Product</th>
                            <th className="text-right px-3 py-2 font-medium text-xs">Qty</th>
                            <th className="text-right px-3 py-2 font-medium text-xs">Per Layer</th>
                            <th className="text-right px-3 py-2 font-medium text-xs">Pallets</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(result.result.palletSummary as PalletSummaryRow[]).map((ps, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="px-3 py-2 text-xs">{ps.brand} {ps.modelName}</td>
                              <td className="px-3 py-2 text-xs text-right">{ps.quantity}</td>
                              <td className="px-3 py-2 text-xs text-right">{ps.unitsPerLayer}</td>
                              <td className="px-3 py-2 text-xs text-right font-semibold">{ps.palletsRequired}</td>
                            </tr>
                          ))}
                          {result.result.palletSummary.length > 1 && (
                            <tr className="border-t bg-secondary/30">
                              <td colSpan={3} className="px-3 py-2 text-xs font-medium text-right">Total pallets:</td>
                              <td className="px-3 py-2 text-xs font-bold text-right">
                                {(result.result.palletSummary as PalletSummaryRow[]).reduce((sum, ps) => sum + ps.palletsRequired, 0)}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 3D Results */}
            {result.mode === '3d' && !result.fallbackUsed && result.result && (
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <PackageOpen className="h-4 w-4" />
                  3D Paxter Optimization Results
                </h3>
                
                <div className="bg-secondary/30 p-4 rounded-lg space-y-3">
                  {/* TODO: Add 3D specific visualizations */}
                  <div className="text-center py-4 text-muted-foreground">
                    <p className="mb-2">3D optimization data received!</p>
                    <pre className="text-xs text-left bg-secondary p-3 rounded overflow-auto max-h-64">
                      {JSON.stringify(result.result, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}

            {/* Recalculate Button */}
            <div className="flex justify-center pt-2">
              <Button 
                onClick={handleCalculate} 
                variant="outline"
                data-testid="button-recalculate"
              >
                Recalculate
              </Button>
            </div>
          </div>
        )}

        {/* Error State */}
        {cartonizationMutation.isError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to calculate truck loading. Please try again.
            </AlertDescription>
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}
